import { useState, useCallback } from "react";

import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { Button, Alert, Tooltip } from "@impero-com/ui";
import { Input } from "antd";

import { ControlWithFieldsView } from "api/generated/model";

import { useSubmitBulkImportMutation } from "../queries/useBulkImportMutation";
import { BulkImportStep } from "../stepsContainer";

import * as theme from "./theme.css";

const CONFIRMATION_TEXT = "IMPERO";

interface ConfirmationStepProps {
    controls: ControlWithFieldsView[] | null;
    onStepChange: (step: BulkImportStep) => void;
}

function getDisabledReasons(isConfirmed: boolean, t: TFunction): string {
    const reasons: string[] = [];

    if (!isConfirmed) {
        reasons.push(
            t("ControlsBulkImport_ConfirmationStep_TooltipTypeConfirmation")
        );
    }

    return reasons.join("\n");
}

export function ConfirmationStep({
    controls,
    onStepChange,
}: ConfirmationStepProps) {
    const { t } = useTranslation();
    const [confirmText, setConfirmText] = useState("");
    const { mutate: submitBulkImport, isPending } =
        useSubmitBulkImportMutation();
    const isConfirmed = confirmText === CONFIRMATION_TEXT;

    const handleSubmit = useCallback(() => {
        if (!controls || !isConfirmed || isPending) {
            return;
        }

        submitBulkImport(
            { data: controls },
            {
                onSuccess: () => {
                    setConfirmText("");
                    onStepChange("export");
                },
            }
        );
    }, [
        controls,
        isConfirmed,
        isPending,
        submitBulkImport,
        setConfirmText,
        onStepChange,
    ]);

    const handleBack = () => {
        onStepChange("upload");
    };

    return (
        <div className={theme.stepContainer}>
            <h2>{t("ControlsBulkImport_ConfirmationStep_Title")}</h2>
            <Alert
                showIcon={true}
                type="info"
                message={t("ControlsBulkImport_ConfirmationStep_Instructions")}
                className={theme.infoAlertBox}
            />

            <div className={theme.confirmSection}>
                <h3>{t("ControlsBulkImport_ConfirmationStep_TypeConfirm")}</h3>
                <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder={CONFIRMATION_TEXT}
                />
            </div>

            <div className={theme.buttonGroup}>
                <Button onClick={handleBack} disabled={isPending}>
                    {t("Back")}
                </Button>
                <Tooltip
                    title={getDisabledReasons(isConfirmed, t)}
                    open={!isConfirmed ? undefined : false}
                >
                    <Button
                        variant="primary"
                        disabled={!isConfirmed || isPending}
                        onClick={handleSubmit}
                        loading={isPending}
                    >
                        {t("ControlsBulkImport_ConfirmationStep_Create")}
                    </Button>
                </Tooltip>
            </div>
        </div>
    );
}
